/**
 * SignupConfirmPage — autenticazione Phase 5 (IMPL-P5-011).
 *
 * Terminal page after a successful signup. Tells the user to check their
 * inbox for the verification email. Provides a "reinvia link" CTA that
 * calls `authClient.resendVerification(email)` — anti-enumeration
 * compliance is enforced server-side, so we surface a generic confirmation
 * regardless of result.
 */
import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { AuthStatusPage } from '../components/AuthStatusPage';
import { authClient } from '../lib/auth-client';
import { SIGNUP_EMAIL_KEY } from './SignupPage';

export function SignupConfirmPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  useEffect(() => {
    try {
      const v = sessionStorage.getItem(SIGNUP_EMAIL_KEY);
      if (v) setEmail(v);
    } catch {
      /* ignore */
    }
  }, []);

  const description = email ? (
    <>
      Abbiamo inviato un'email a <strong className="font-medium">{email}</strong>. Apri il link
      per attivare l'account. Controlla anche la cartella Spam.
    </>
  ) : (
    "Abbiamo inviato un'email all'indirizzo che hai indicato. Apri il link per attivare l'account. Controlla anche la cartella Spam."
  );

  async function handleResend() {
    if (!email || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await authClient.resendVerification(email);
    } catch {
      /* Anti-enumeration: success/failure look identical to the user. */
    } finally {
      setResendStatus('sent');
    }
  }

  return (
    <AuthStatusPage
      icon={Mail}
      tone="info"
      title="Controlla la tua casella"
      description={description}
      primaryAction={{
        label:
          resendStatus === 'sending'
            ? 'Invio in corso…'
            : resendStatus === 'sent'
              ? 'Link reinviato'
              : 'Reinvia link',
        onClick: handleResend,
      }}
      secondaryAction={{ label: 'Torna al login', to: '/accedi' }}
    />
  );
}
